<template>
  <main class="w-full max-w-none">
    <div class="mx-auto w-full max-w-full px-0">
      <div class="mb-8">
        <h1 class="text-2xl md:text-3xl font-bold text-gray-900 mb-2">跨境支付制单</h1>
        <AdvancedFeatureNotice api-url="/api/v2/payment/crossPay" />
        <p class="text-gray-600">
          提交国际支付指令（境内外币支付、跨境支付，POST /api/v2/payment/crossPay）。支付状态请使用「查询支付状态」接口。
        </p>
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-5 gap-3 w-full">
        <!-- 左侧：接口文档 -->
        <div class="w-full lg:col-span-3">
          <Card class="mb-8">
            <CardHeader>
              <CardTitle>接口说明</CardTitle>
              <CardDescription>
                跨境支付制单接口，用于提交境内外币支付、跨境支付订单（请求体为嵌套 JSON，与对外 API 文档一致）。
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div class="flex items-center gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
                <span class="px-3 py-1 bg-green-100 text-green-800 text-sm font-medium rounded-full">POST</span>
                <code class="text-sm font-mono">/api/v2/payment/crossPay</code>
              </div>

              <div class="space-y-6">
                <div>
                  <h3 class="text-lg font-medium text-gray-900 mb-3">
                    请求头参数
                  </h3>
                  <div class="overflow-x-auto">
                    <table class="min-w-max md:min-w-full divide-y divide-gray-200">
                      <thead class="bg-gray-50">
                        <tr>
                          <th
                            class="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            参数名
                          </th>
                          <th
                            class="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            类型
                          </th>
                          <th
                            class="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            必需
                          </th>
                          <th
                            class="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            描述
                          </th>
                          <th
                            class="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            示例值
                          </th>
                        </tr>
                      </thead>
                      <tbody class="bg-white divide-y divide-gray-200">
                        <tr>
                          <td
                            class="px-3 md:px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                          >
                            x-app-id
                          </td>
                          <td
                            class="px-3 md:px-6 py-4 whitespace-nowrap text-sm text-gray-500"
                          >
                            String
                          </td>
                          <td
                            class="px-3 md:px-6 py-4 whitespace-nowrap text-sm text-gray-500"
                          >
                            是
                          </td>
                          <td class="px-3 md:px-6 py-4 text-sm text-gray-500">
                            APP ID
                          </td>
                          <td class="px-3 md:px-6 py-4 text-sm text-gray-500">
                            4d9414e89bc24b0d89b678d9f20bc56a
                          </td>
                        </tr>
                        <tr>
                          <td
                            class="px-3 md:px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                          >
                            x-app-secret
                          </td>
                          <td
                            class="px-3 md:px-6 py-4 whitespace-nowrap text-sm text-gray-500"
                          >
                            String
                          </td>
                          <td
                            class="px-3 md:px-6 py-4 whitespace-nowrap text-sm text-gray-500"
                          >
                            是
                          </td>
                          <td class="px-3 md:px-6 py-4 text-sm text-gray-500">
                            API Key / AppSecret
                          </td>
                          <td class="px-3 md:px-6 py-4 text-sm text-gray-500">
                            8201e508ef948b8fcdca855729e7ee34
                          </td>
                        </tr>
                        <tr>
                          <td
                            class="px-3 md:px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                          >
                            x-customer-code
                          </td>
                          <td
                            class="px-3 md:px-6 py-4 whitespace-nowrap text-sm text-gray-500"
                          >
                            String
                          </td>
                          <td
                            class="px-3 md:px-6 py-4 whitespace-nowrap text-sm text-gray-500"
                          >
                            是
                          </td>
                          <td class="px-3 md:px-6 py-4 text-sm text-gray-500">
                            客户编号
                          </td>
                          <td class="px-3 md:px-6 py-4 text-sm text-gray-500">
                            CUSTFA48DD8EFF437956
                          </td>
                        </tr>
                        <tr>
                          <td
                            class="px-3 md:px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                          >
                            Content-Type
                          </td>
                          <td
                            class="px-3 md:px-6 py-4 whitespace-nowrap text-sm text-gray-500"
                          >
                            String
                          </td>
                          <td
                            class="px-3 md:px-6 py-4 whitespace-nowrap text-sm text-gray-500"
                          >
                            是
                          </td>
                          <td class="px-3 md:px-6 py-4 text-sm text-gray-500">
                            发送内容类型
                          </td>
                          <td class="px-3 md:px-6 py-4 text-sm text-gray-500">
                            application/json
                          </td>
                        </tr>
                        <tr>
                          <td
                            class="px-3 md:px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                          >
                            xencio-key
                          </td>
                          <td
                            class="px-3 md:px-6 py-4 whitespace-nowrap text-sm text-gray-500"
                          >
                            String
                          </td>
                          <td
                            class="px-3 md:px-6 py-4 whitespace-nowrap text-sm text-gray-500"
                          >
                            否
                          </td>
                          <td class="px-3 md:px-6 py-4 text-sm text-gray-500">
                            加密密钥，当启用国密加密的时候该请求头必传
                          </td>
                          <td class="px-3 md:px-6 py-4 text-sm text-gray-500">
                            BM/UBCOn2yrZINuOdvaS01KjftyyRagN/LU2zVntwD8svrIObu/2Sqjl041C3RyqsUlE02ERmRBNgoPcoyhM7Tm1Quv55QcQ9hDNX0GO3997OANU7HQGHYlJ2aDEf0pTGNC0Lu5CzAwSM76dTorLyGI=
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <!-- 请求参数（分组） -->
                <div v-for="group in requestParamGroups" :key="group.title">
                  <h3 class="text-lg font-medium text-gray-900 mb-3">{{ group.title }}</h3>
                  <div class="overflow-x-auto max-h-[600px] overflow-y-auto">
                    <table class="min-w-max md:min-w-full divide-y divide-gray-200">
                      <thead class="bg-gray-50 sticky top-0">
                        <tr>
                          <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">参数名</th>
                          <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">类型</th>
                          <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">必需</th>
                          <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">描述</th>
                          <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">示例值</th>
                        </tr>
                      </thead>
                      <tbody class="bg-white divide-y divide-gray-200">
                        <tr v-for="param in group.params" :key="param.name">
                          <td class="px-4 py-3 text-sm font-medium text-gray-900">{{ param.name }}</td>
                          <td class="px-4 py-3 text-sm text-gray-500">{{ param.type }}</td>
                          <td class="px-4 py-3 text-sm text-gray-500">
                            <span :class="param.required === '是' ? 'font-medium' : ''">{{ param.required }}</span>
                          </td>
                          <td class="px-4 py-3 text-sm text-gray-500">{{ param.description }}</td>
                          <td class="px-4 py-3 text-sm text-gray-500">{{ param.example || '-' }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <!-- 响应参数 -->
                <div>
                  <h3 class="text-lg font-medium text-gray-900 mb-3">响应参数</h3>
                  <div class="overflow-x-auto">
                    <table class="min-w-max md:min-w-full divide-y divide-gray-200">
                      <thead class="bg-gray-50">
                        <tr>
                          <th class="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">字段</th>
                          <th class="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">类型</th>
                          <th class="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">说明</th>
                        </tr>
                      </thead>
                      <tbody class="bg-white divide-y divide-gray-200">
                        <tr v-for="r in responseParams" :key="r.field">
                          <td class="px-3 md:px-6 py-4 text-sm font-medium text-gray-900">{{ r.field }}</td>
                          <td class="px-3 md:px-6 py-4 text-sm text-gray-500">{{ r.type }}</td>
                          <td class="px-3 md:px-6 py-4 text-sm text-gray-500">{{ r.description }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <!-- 请求示例 -->
                <div>
                  <h3 class="text-lg font-medium text-gray-900 mb-3">请求示例</h3>
                  <pre class="bg-gray-800 text-gray-100 p-4 rounded-md text-sm overflow-x-auto"><code class="language-http">POST /api/v2/payment/crossPay
Content-Type: application/json

{
  "businessTransNo": "EXT-20260519-0000001",
  "payAmount": "12345.67",
  "payCurrency": "USD",
  "remitType": "0",
  "crossBank": "0",
  "crossCity": "0",
  "scheduleDate": "20260520",
  "bankApprovalFlag": "0",
  "purpose": "货款",
  "priority": "1",
  "eBopFlag": "0",
  "fromAccountInfo": {
    "fromAccountNo": "6228480000000001234",
    "fromAccountName": "测试企业A",
    "fromAccountCurrency": "USD",
    "fromAccountType": "00"
  },
  "fromBankInfo": {
    "fromBankSwiftCode": "ICBKCNBJ",
    "fromBankName": "ICBC",
    "fromBankCountry": "CN"
  },
  "payerInfo": {
    "fromUnitCode": "91310000MA1FL0000X"
  },
  "toAccountInfo": {
    "toAccountNo": "1234567890",
    "toAccountName": "Apple Inc.",
    "toAccountType": "0",
    "toAccountCurrency": "USD"
  },
  "toBankInfo": {
    "toBankSwiftCode": "BOFAUS3N",
    "toBankName": "BANK OF AMERICA",
    "toBankCountry": "US"
  },
  "feeInfo": { "feePayer": "1", "feePayType": "3" },
  "metaJson": "{\"projectInfo\":\"测试项目信息\"}"
}</code></pre>
                </div>

                <!-- 成功响应示例 -->
                <div>
                  <h3 class="text-lg font-medium text-gray-900 mb-3">成功响应示例</h3>
                  <pre class="bg-gray-800 text-gray-100 p-4 rounded-md text-sm overflow-x-auto"><code class="language-json">{
  "status": "OK",
  "code": null,
  "message": "付款指令已提交，后续请根据支付状态查询接口查询支付结果",
  "data": {
    "businessTransNo": "EXT-20260428-0000001",
    "paySerialNo": "PAY20260428000001"
  }
}</code></pre>
                </div>

                <!-- 失败响应示例 -->
                <div>
                  <h3 class="text-lg font-medium text-gray-900 mb-3">失败响应示例</h3>
                  <pre class="bg-gray-800 text-gray-100 p-4 rounded-md text-sm overflow-x-auto"><code class="language-json">{
  "status": "OK",
  "code": "400",
  "message": "付款金额必须大于0",
  "data": null
}</code></pre>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <!-- 右侧"试一试"区域 -->
        <div class="w-full lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>试一试</CardTitle>
              <CardDescription>使用不同语言测试API</CardDescription>
            </CardHeader>
            <CardContent>
              <div class="border-b border-gray-200">
                <nav class="-mb-px flex space-x-8 overflow-x-auto">
                  <button
                    v-for="language in languages"
                    :key="language.id"
                    @click="activeLanguage = language.id"
                    class="whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm"
                    :class="[
                      activeLanguage === language.id
                        ? 'border-[var(--primary)] text-[var(--primary)]'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
                    ]"
                  >
                    {{ language.name }}
                  </button>
                </nav>
              </div>

              <div class="mt-6">
                <!-- 国密加密开关 -->
                <label class="inline-flex items-center gap-2 mb-3 select-none">
                  <input
                    type="checkbox"
                    v-model="enableGm"
                    class="rounded border-gray-300 text-[var(--primary)] focus:ring-[var(--primary)]"
                  />
                  <span
                    class="text-sm"
                    :class="enableGm ? 'text-[var(--primary)] font-medium' : 'text-gray-700'"
                    >启用国密加密（SM2/SM3/SM4）</span
                  >
                  <button
                    type="button"
                    class="ml-1 inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-100 text-blue-700 text-xs font-bold hover:bg-blue-200"
                    @click="showGmHelp = true"
                    aria-label="查看国密加密传输说明"
                  >
                    ?
                  </button>
                </label>
                <div ref="trySampleCodeRoot">
                  <pre
                    v-if="!enableGm"
                    class="bg-gray-800 text-gray-100 p-4 rounded-md text-sm overflow-x-auto"
                  ><code :key="`plain-${activeLanguage}`">{{ getRequestText() }}</code></pre>
                  <pre
                    v-if="enableGm"
                    class="bg-gray-900 text-gray-100 p-4 rounded-md text-sm overflow-x-auto mt-3"
                  ><code :key="`gm-${activeLanguage}`">{{ getGmSampleText() }}</code></pre>
                </div>
                <!-- 国密说明弹框 -->
                <div v-if="showGmHelp" class="fixed inset-0 z-50">
                  <div
                    class="absolute inset-0 bg-black/40"
                    @click="showGmHelp = false"
                  ></div>
                  <div
                    class="absolute inset-0 flex items-center justify-center p-4"
                  >
                    <div
                      class="w-full max-w-[90vw] md:max-w-2xl rounded-lg bg-white shadow-xl max-h-[85vh] flex flex-col"
                    >
                      <div
                        class="px-4 py-3 border-b border-gray-200 flex items-center justify-end"
                      >
                        <h3 class="sr-only">国密加密传输说明</h3>
                        <button
                          type="button"
                          class="p-1 rounded hover:bg-gray-100"
                          @click="showGmHelp = false"
                        >
                          ✕
                        </button>
                      </div>
                      <div class="px-4 py-4 overflow-y-auto">
                        <GmHelpContent />
                      </div>
                      <div
                        class="px-4 py-3 border-t border-gray-200 flex justify-end"
                      >
                        <button
                          type="button"
                          class="px-3 py-1.5 rounded bg-[var(--primary)] text-white hover:opacity-90"
                          @click="showGmHelp = false"
                        >
                          关闭
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- 国密加密开关与密钥输入 -->
                <div
                  v-if="enableGm"
                  class="pt-2 border-t border-gray-200"
                  style="margin-bottom: 0.5rem"
                >
                  <div
                    class="mt-4 rounded-lg border border-gray-200 bg-gray-50"
                  >
                    <div class="flex items-center justify-between px-3 py-2">
                      <div class="text-sm font-medium text-gray-800">
                        国密加密传输（SM2/SM3/SM4）
                      </div>
                      <span
                        v-if="enableGm"
                        class="text-xs px-2 py-0.5 rounded bg-green-100 text-green-700"
                        >已启用</span
                      >
                    </div>
                    <div class="flex items-center justify-between px-3 py-2">
                      <a
                        :href="getBaseDomain() + '/#/app-params'"
                        target="_blank"
                        class="card-link"
                        style="
                          color: var(--primary);
                          font-weight: 500;
                          font-style: italic;
                        "
                      >
                        <KeyOutlined class="w-4 h-4" />
                        获取/配置密钥
                      </a>
                    </div>
                    <div class="px-3 pb-3">
                      <div class="grid grid-cols-1 gap-3">
                        <div>
                          <label
                            class="block text-sm font-medium text-gray-700 mb-1"
                            >平台公钥（SM2 公钥，Base64）</label
                          >
                          <textarea
                            v-model="xencioPublicKey"
                            rows="3"
                            class="w-full px-3 py-2 font-mono tracking-wider border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[var(--primary)] focus:border-[var(--primary)] resize-y"
                            placeholder="粘贴见知提供的 SM2 公钥（Base64，标准编码）"
                          ></textarea>
                          <div class="mt-1 flex items-center justify-between">
                            <p class="text-xs text-gray-500">
                              用于加密
                              <code class="rounded bg-violet-50 px-1 py-0.5 font-mono text-[11px] text-violet-800">reqData</code>、<code class="rounded bg-violet-50 px-1 py-0.5 font-mono text-[11px] text-violet-800">sign</code>
                              以及 Header
                              <code class="rounded bg-violet-50 px-1 py-0.5 font-mono text-[11px] text-violet-800">xencio-key</code>。
                            </p>
                            <div class="flex items-center gap-2">
                              <span
                                v-if="
                                  xencioPublicKey && isBase64(xencioPublicKey)
                                "
                                class="text-xs px-1.5 py-0.5 rounded bg-green-100 text-green-700"
                                >Base64格式正确</span
                              >
                              <span
                                v-else-if="
                                  xencioPublicKey && !isBase64(xencioPublicKey)
                                "
                                class="text-xs px-1.5 py-0.5 rounded bg-red-100 text-red-700"
                                >Base64格式错误</span
                              >
                              <button
                                type="button"
                                class="text-xs text-gray-500 hover:text-gray-700"
                                @click="xencioPublicKey = ''"
                              >
                                清空
                              </button>
                            </div>
                          </div>
                        </div>
                        <div>
                          <label
                            class="block text-sm font-medium text-gray-700 mb-1"
                            >企业 SM2 私钥（Base64）</label
                          >
                          <textarea
                            v-model="sm2PrivateKey"
                            rows="3"
                            class="w-full px-3 py-2 font-mono tracking-wider border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[var(--primary)] focus:border-[var(--primary)] resize-y"
                            placeholder="粘贴企业自有 SM2 私钥（Base64，标准编码）"
                          ></textarea>
                          <div class="mt-1 flex items-center justify-between">
                            <p class="text-xs text-gray-500">
                              用于解密响应
                              <code class="rounded bg-violet-50 px-1 py-0.5 font-mono text-[11px] text-violet-800">respData</code>、<code class="rounded bg-violet-50 px-1 py-0.5 font-mono text-[11px] text-violet-800">sign</code>
                              以及 Header
                              <code class="rounded bg-violet-50 px-1 py-0.5 font-mono text-[11px] text-violet-800">xencio-key</code>。
                            </p>
                            <div class="flex items-center gap-2">
                              <span
                                v-if="sm2PrivateKey && isBase64(sm2PrivateKey)"
                                class="text-xs px-1.5 py-0.5 rounded bg-green-100 text-green-700"
                                >Base64格式正确</span
                              >
                              <span
                                v-else-if="
                                  sm2PrivateKey && !isBase64(sm2PrivateKey)
                                "
                                class="text-xs px-1.5 py-0.5 rounded bg-red-100 text-red-700"
                                >Base64格式错误</span
                              >
                              <button
                                type="button"
                                class="text-xs text-gray-500 hover:text-gray-700"
                                @click="sm2PrivateKey = ''"
                              >
                                清空
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="space-y-4 mt-6">
                  <!-- Header 参数 -->
                  <div class="border-b border-gray-200 pb-4 mb-2">
                    <h4 class="text-sm font-medium text-gray-900 mb-3">请求头参数</h4>
                    <div>
                      <label class="block text-sm font-medium text-gray-700 mb-1">x-app-id <span class="text-red-500">*</span></label>
                      <input v-model="appId" type="text" :class="{ '!border-red-500': showValidation && !appId }" placeholder="请输入 x-app-id"
                        class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[var(--primary)] focus:border-[var(--primary)]" />
                    </div>
                    <div class="mt-3">
                      <label class="block text-sm font-medium text-gray-700 mb-1">x-app-secret <span class="text-red-500">*</span></label>
                      <input v-model="appSecret" type="text" :class="{ '!border-red-500': showValidation && !appSecret }" placeholder="请输入 x-app-secret"
                        class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[var(--primary)] focus:border-[var(--primary)]" />
                    </div>
                    <div class="mt-3">
                      <label class="block text-sm font-medium text-gray-700 mb-1">x-customer-code <span class="text-red-500">*</span></label>
                      <input v-model="customerCode" type="text" :class="{ '!border-red-500': showValidation && !customerCode }" placeholder="请输入 x-customer-code"
                        class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[var(--primary)] focus:border-[var(--primary)]" />
                    </div>
                  </div>

                  <!-- 业务参数 -->
                  <div v-for="group in formGroups" :key="group.title">
                    <h4 class="text-sm font-semibold mb-2 text-gray-700">{{ group.title }}</h4>
                    <div class="mb-3 rounded-lg">
                      <div class="grid grid-cols-1 gap-3">
                        <div v-for="field in group.fields" :key="joinFieldPath(group.pathPrefix, field.path)">
                          <label class="block text-sm font-medium text-gray-700 mb-1">
                            {{ field.path }}
                            <span v-if="field.required" class="text-red-500">*</span>
                            <span v-else-if="field.conditional" class="text-amber-500 text-xs">(条件)</span>
                          </label>
                          <select
                            v-if="field.control === 'select'"
                            :value="fieldGet(joinFieldPath(group.pathPrefix, field.path))"
                            @change="fieldSet(joinFieldPath(group.pathPrefix, field.path), ($event.target as HTMLSelectElement).value)"
                            class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[var(--primary)] focus:border-[var(--primary)]"
                          >
                            <option v-for="opt in field.options" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
                          </select>
                          <input
                            v-else
                            :value="fieldGet(joinFieldPath(group.pathPrefix, field.path))"
                            type="text"
                            :placeholder="field.placeholder"
                            @input="fieldSet(joinFieldPath(group.pathPrefix, field.path), ($event.target as HTMLInputElement).value)"
                            class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[var(--primary)] focus:border-[var(--primary)]"
                          />
                          <p v-if="field.hint" class="text-xs text-gray-500 mt-1">{{ field.hint }}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="mb-3 rounded-lg">
                    <label class="block text-sm font-medium text-gray-700">metaJson</label>
                    <textarea
                      v-model="metaJson"
                      rows="3"
                      placeholder='{"projectInfo":"测试项目信息"}'
                      class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[var(--primary)] focus:border-[var(--primary)]"
                    ></textarea>
                    <p class="text-xs text-gray-500 mt-1">留空则不传 metaJson；须为 JSON 字符串。</p>
                  </div>

                  <button
                    @click="submitPayment"
                    :disabled="loading"
                    class="w-full bg-[var(--primary)] hover:bg-[color:var(--primary)] text-white font-medium py-2 px-4 rounded-md transition duration-200 ease-in-out transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-[var(--primary)] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {{ loading ? "提交中..." : "发起跨境支付制单请求" }}
                  </button>
                </div>
              </div>

              <!-- 响应结果 -->
              <div v-if="response" ref="tryResponseCodeRoot" class="mt-6">
                <h3 class="text-lg font-medium text-gray-900 mb-2">响应结果</h3>
                <pre
                  class="bg-gray-800 text-gray-100 p-4 rounded-md text-sm overflow-x-auto"
                ><code>{{ response }}</code></pre>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  </main>
</template>

<script setup lang="ts">
import GmHelpContent from "./ui/GmHelpContent.vue";
import { ref, onMounted, watch, nextTick } from "vue";
import Card from "./ui/Card.vue";
import CardHeader from "./ui/CardHeader.vue";
import CardTitle from "./ui/CardTitle.vue";
import CardDescription from "./ui/CardDescription.vue";
import CardContent from "./ui/CardContent.vue";
import AdvancedFeatureNotice from "./ui/AdvancedFeatureNotice.vue";
import httpService from "@/lib/http";
import hljs from "highlight.js/lib/core";
import { useSystemStore } from "@/stores/system";
import { getBaseDomain } from "@/lib/config";
import * as sm from "sm-crypto";
import { get as lodashGet, set as lodashSet } from "lodash";

const serverUrl = import.meta.env.VITE_API_URL;
const loading = ref(false);
const response = ref("");
const activeLanguage = ref("curl");

// ── 请求参数分组（文档展示，与对外 API 一致） ──
const requestParamGroups = [
  {
    title: "根级字段",
    params: [
      { name: "businessTransNo", type: "string", required: "是", description: "外部交易流水号，业务方维度全局唯一", example: "EXT-20260519-0000001" },
      { name: "payAmount", type: "string", required: "是", description: "付款金额，最多两位小数，必须 > 0", example: "12345.67" },
      { name: "payCurrency", type: "string", required: "是", description: "付款币别", example: "USD" },
      { name: "remitType", type: "string", required: "否", description: "汇款类型：0=现汇（默认） 1=购汇 2=其他", example: "0" },
      { name: "purchaseAmount", type: "string", required: "否", description: "购汇金额；remitType=1 时必填且大于 0", example: "" },
      { name: "crossBank", type: "string", required: "否", description: "本行/他行标志：0=本行，1=他行", example: "0" },
      { name: "crossCity", type: "string", required: "否", description: "同城/异地标志：0=同城，1=异地", example: "0" },
      { name: "scheduleDate", type: "string", required: "是", description: "计划付款日期，格式 yyyyMMdd", example: "20260520" },
      { name: "bankApprovalFlag", type: "string", required: "否", description: "是否网银复核：0=直接支付（默认），1=需要网银复核", example: "0" },
      { name: "purpose", type: "string", required: "否", description: "付款用途", example: "货款" },
      { name: "postscript", type: "string", required: "否", description: "附言", example: "" },
      { name: "summary", type: "string", required: "否", description: "摘要", example: "" },
      { name: "priority", type: "string", required: "否", description: "优先级：1=普通（默认），2=加急", example: "1" },
      { name: "eBopFlag", type: "string", required: "否", description: "是否进行 BOP 申报：0=不申报（默认），1=申报", example: "0" },
      { name: "ignoreCheckBop", type: "string", required: "否", description: "传 1 时忽略 BOP 校验，并将 eBopFlag 置为 0", example: "" },
      { name: "metaJson", type: "string", required: "否", description: "扩展字段 注：json字符串; projectInfo-项目信息", example: '{"projectInfo":"测试项目信息"}' },
    ],
  },
  {
    title: "fromAccountInfo",
    params: [
      { name: "fromAccountNo", type: "string", required: "是", description: "付款账号", example: "" },
      { name: "fromAccountName", type: "string", required: "是", description: "付款账户名", example: "" },
      { name: "fromAccountEnglishName", type: "string", required: "否", description: "账户英文名称", example: "" },
      { name: "fromAccountCurrency", type: "string", required: "是", description: "付款账户币别", example: "USD" },
      { name: "fromAccountType", type: "string", required: "否", description: "付款账户类型：00=对公（默认），01=对私", example: "00" },
    ],
  },
  {
    title: "fromBankInfo",
    params: [
      { name: "fromBankSwiftCode", type: "string", required: "是", description: "付款方 SWIFT/BIC", example: "ICBKCNBJ" },
      { name: "fromBankLocalCode", type: "string", required: "否", description: "付款账号联行号（地方清算）", example: "" },
      { name: "fromBankBranchCode", type: "string", required: "否", description: "付款方分行代码", example: "" },
      { name: "fromBankName", type: "string", required: "是", description: "付款账户银行", example: "" },
      { name: "fromBankBranchName", type: "string", required: "否", description: "付款账户银行分行名", example: "" },
      { name: "fromBankCountry", type: "string", required: "是", description: "付款账户开户地", example: "CN" },
      { name: "fromBankAddress", type: "string", required: "否", description: "付款账户开户行地址", example: "" },
    ],
  },
  {
    title: "payerInfo",
    params: [
      { name: "fromCountry", type: "string", required: "否", description: "付款人所在国", example: "" },
      { name: "fromState", type: "string", required: "否", description: "州/省", example: "" },
      { name: "fromCity", type: "string", required: "否", description: "城市", example: "" },
      { name: "fromAddress", type: "string", required: "否", description: "地址", example: "" },
      { name: "fromUnitCode", type: "string", required: "否", description: "付款人组织机构代码（fromAccountType=00 时必填）", example: "" },
      { name: "fromIndividualNo", type: "string", required: "否", description: "付款人个人身份证件号码（fromAccountType=01 时必填）", example: "" },
      { name: "fromIndividualType", type: "string", required: "否", description: "付款人证件类型（fromAccountType=01 时必填）", example: "" },
    ],
  },
  {
    title: "toAccountInfo",
    params: [
      { name: "toAccountNo", type: "string", required: "是", description: "收款账号", example: "" },
      { name: "toAccountName", type: "string", required: "是", description: "收款账户名", example: "" },
      { name: "toAccountType", type: "string", required: "是", description: "0 对公 / 1 对私", example: "0" },
      { name: "toAccountCurrency", type: "string", required: "是", description: "收款账户币别", example: "USD" },
    ],
  },
  {
    title: "toBankInfo",
    params: [
      { name: "toBankSwiftCode", type: "string", required: "否", description: "收款方 SWIFT/BIC", example: "" },
      { name: "toBankLocalCode", type: "string", required: "否", description: "收款行行号/CNAPS", example: "" },
      { name: "toBankBranchCode", type: "string", required: "否", description: "收款行分行代码", example: "" },
      { name: "toBankName", type: "string", required: "否", description: "收款行名称", example: "" },
      { name: "toBankCountry", type: "string", required: "否", description: "收款账户所在国", example: "US" },
      { name: "toBankCountryName", type: "string", required: "否", description: "收款账户国家名称", example: "" },
      { name: "toBankAddress", type: "string", required: "否", description: "收款行地址", example: "" },
    ],
  },
  {
    title: "payeeInfo",
    params: [
      { name: "toCountry", type: "string", required: "否", description: "收款人所在国", example: "" },
      { name: "toCountryName", type: "string", required: "否", description: "收款人国家名称", example: "" },
      { name: "toState", type: "string", required: "否", description: "州/省", example: "" },
      { name: "toCity", type: "string", required: "否", description: "城市", example: "" },
      { name: "toAddress", type: "string", required: "否", description: "地址", example: "" },
      { name: "toEmail", type: "string", required: "否", description: "邮箱", example: "" },
      { name: "toPhone", type: "string", required: "否", description: "电话", example: "" },
    ],
  },
  {
    title: "feeInfo",
    params: [
      { name: "feePayer", type: "string", required: "否", description: "费用承担方：1=汇款人（默认），2=收款人，3=共同", example: "1" },
      { name: "feePayType", type: "string", required: "否", description: "费用交付方式：1=现金，2=支票，3=账户（默认）", example: "3" },
    ],
  },
  {
    title: "applicantInfo",
    params: [
      { name: "applicantName", type: "string", required: "否", description: "申请人姓名", example: "" },
      { name: "applicantTel", type: "string", required: "否", description: "申请人电话", example: "" },
    ],
  },
  {
    title: "eBopInfo",
    params: [
      { name: "paymentType", type: "string", required: "否", description: "付款类型：O=海外外币，D=国内外币，X=跨境人民币", example: "" },
      { name: "unitCode", type: "string", required: "否", description: "主体标识码/组织机构代码", example: "" },
      { name: "residentCountry", type: "string", required: "否", description: "收款人常驻国家（地区）代码", example: "" },
      { name: "residentCountryName", type: "string", required: "否", description: "收款人常驻国家名称（运行时回填）", example: "" },
      { name: "paymentUnderBondedGoods", type: "string", required: "否", description: "是否为报税货物项下付款：Y=是，N=否", example: "" },
      { name: "paymentTerm", type: "string", required: "否", description: "付款方式：A=预付货款，P=货到付款，R=退款，O=其他", example: "" },
      { name: "paymentNature", type: "string", required: "否", description: "付款性质：X=保税区，E=出口加工区，D=钻石交易所，A=其他经济特区，M=深加工结转，O=其他", example: "" },
      { name: "transactionCode1", type: "string", required: "否", description: "BOP 交易代码 1", example: "" },
      { name: "transactionRemark1", type: "string", required: "否", description: "交易备注 1", example: "" },
      { name: "amount1", type: "string", required: "否", description: "金额 1", example: "" },
      { name: "currency1", type: "string", required: "否", description: "币种 1", example: "" },
      { name: "transactionCode2", type: "string", required: "否", description: "BOP 交易代码 2", example: "" },
      { name: "transactionRemark2", type: "string", required: "否", description: "交易备注 2", example: "" },
      { name: "amount2", type: "string", required: "否", description: "金额 2", example: "" },
      { name: "currency2", type: "string", required: "否", description: "币种 2", example: "" },
      { name: "contractNo", type: "string", required: "否", description: "合同号", example: "" },
      { name: "invoiceNo", type: "string", required: "否", description: "发票编号", example: "" },
      { name: "safeApprovalNo", type: "string", required: "否", description: "备案号/安全批准号/参考号", example: "" },
    ],
  },
];

// ── 响应参数（文档展示） ──
const responseParams = [
  { field: "status", type: "String", description: "OK 表示请求处理完成（业务是否成功看 code）" },
  { field: "code", type: "String | null", description: "成功为 null；业务异常为 \"400\"" },
  { field: "message", type: "String", description: "成功为提示语，异常为错误描述" },
  { field: "data.businessTransNo", type: "String", description: "业务方传入的外部交易流水号（原样返回）" },
  { field: "data.paySerialNo", type: "String", description: "系统生成的内部交易流水号，用于后续支付状态查询" },
];

// 「试一试」内动态代码块（避免误处理左侧文档里带 language-* 的静态示例）
const trySampleCodeRoot = ref<HTMLElement | null>(null);
const tryResponseCodeRoot = ref<HTMLElement | null>(null);

// ── 代码高亮（与 paymentSubmitPage 一致：随语言 / 国密开关刷新） ──
const highlightCode = () => {
  nextTick(() => {
    setTimeout(() => {
      const roots = [trySampleCodeRoot.value, tryResponseCodeRoot.value].filter(
        Boolean
      ) as HTMLElement[];
      const blocks: HTMLElement[] = [];
      roots.forEach((root) => {
        root.querySelectorAll("pre code").forEach((el) => {
          blocks.push(el as HTMLElement);
        });
      });
      if (!blocks.length) return;

      const languageMap: Record<string, string> = {
        curl: "language-bash",
        javascript: "language-javascript",
        python: "language-python",
        java: "language-java",
        csharp: "language-csharp",
        php: "language-php",
        ruby: "language-ruby",
      };
      const langClass = languageMap[activeLanguage.value];
      blocks.forEach((block) => {
        block.className = "";
        block.removeAttribute("data-highlighted");
        block.classList.add("hljs");
        if (langClass) {
          block.classList.add(langClass);
        }
        try {
          hljs.highlightElement(block);
        } catch (error) {
          console.warn("代码高亮失败:", error);
        }
      });
    }, 100);
  });
};

highlightCode();

// 系统配置
const systemStore = useSystemStore();

// 默认值
const DEFAULT_APP_ID = "登录后自动获取";
const DEFAULT_APP_SECRET = "登录后自动获取";
const DEFAULT_CUSTOMER_CODE = "登录后自动获取";
const DEFAULT_XENCIO_PUBLIC_KEY = "登录后自动获取";

const appId = ref(DEFAULT_APP_ID);
const appSecret = ref(DEFAULT_APP_SECRET);
const customerCode = ref(DEFAULT_CUSTOMER_CODE);
const xencioPublicKey = ref(DEFAULT_XENCIO_PUBLIC_KEY);
const showValidation = ref(false);
const metaJson = ref("");

type CrossPayFormState = {
  businessTransNo: string;
  payAmount: string;
  payCurrency: string;
  remitType: string;
  purchaseAmount: string;
  crossBank: string;
  crossCity: string;
  scheduleDate: string;
  bankApprovalFlag: string;
  purpose: string;
  postscript: string;
  summary: string;
  priority: string;
  eBopFlag: string;
  ignoreCheckBop: string;
  fromAccountInfo: {
    fromAccountNo: string;
    fromAccountName: string;
    fromAccountEnglishName: string;
    fromAccountCurrency: string;
    fromAccountType: string;
  };
  fromBankInfo: {
    fromBankSwiftCode: string;
    fromBankLocalCode: string;
    fromBankBranchCode: string;
    fromBankName: string;
    fromBankBranchName: string;
    fromBankCountry: string;
    fromBankAddress: string;
  };
  payerInfo: {
    fromCountry: string;
    fromState: string;
    fromCity: string;
    fromAddress: string;
    fromUnitCode: string;
    fromIndividualNo: string;
    fromIndividualType: string;
  };
  toAccountInfo: {
    toAccountNo: string;
    toAccountName: string;
    toAccountType: string;
    toAccountCurrency: string;
  };
  toBankInfo: {
    toBankSwiftCode: string;
    toBankLocalCode: string;
    toBankBranchCode: string;
    toBankName: string;
    toBankCountry: string;
    toBankCountryName: string;
    toBankAddress: string;
  };
  payeeInfo: {
    toCountry: string;
    toCountryName: string;
    toState: string;
    toCity: string;
    toAddress: string;
    toEmail: string;
    toPhone: string;
  };
  feeInfo: {
    feePayer: string;
    feePayType: string;
  };
  applicantInfo: {
    applicantName: string;
    applicantTel: string;
  };
  eBopInfo: {
    paymentType: string;
    unitCode: string;
    residentCountry: string;
    residentCountryName: string;
    paymentUnderBondedGoods: string;
    paymentTerm: string;
    paymentNature: string;
    transactionCode1: string;
    transactionRemark1: string;
    amount1: string;
    currency1: string;
    transactionCode2: string;
    transactionRemark2: string;
    amount2: string;
    currency2: string;
    contractNo: string;
    invoiceNo: string;
    safeApprovalNo: string;
  };
};

const initialFormState = (): CrossPayFormState => ({
  businessTransNo: "",
  payAmount: "",
  payCurrency: "",
  remitType: "0",
  purchaseAmount: "",
  crossBank: "",
  crossCity: "",
  scheduleDate: "",
  bankApprovalFlag: "0",
  purpose: "",
  postscript: "",
  summary: "",
  priority: "1",
  eBopFlag: "0",
  ignoreCheckBop: "",
  fromAccountInfo: {
    fromAccountNo: "",
    fromAccountName: "",
    fromAccountEnglishName: "",
    fromAccountCurrency: "",
    fromAccountType: "00",
  },
  fromBankInfo: {
    fromBankSwiftCode: "",
    fromBankLocalCode: "",
    fromBankBranchCode: "",
    fromBankName: "",
    fromBankBranchName: "",
    fromBankCountry: "",
    fromBankAddress: "",
  },
  payerInfo: {
    fromCountry: "",
    fromState: "",
    fromCity: "",
    fromAddress: "",
    fromUnitCode: "",
    fromIndividualNo: "",
    fromIndividualType: "",
  },
  toAccountInfo: {
    toAccountNo: "",
    toAccountName: "",
    toAccountType: "0",
    toAccountCurrency: "",
  },
  toBankInfo: {
    toBankSwiftCode: "",
    toBankLocalCode: "",
    toBankBranchCode: "",
    toBankName: "",
    toBankCountry: "",
    toBankCountryName: "",
    toBankAddress: "",
  },
  payeeInfo: {
    toCountry: "",
    toCountryName: "",
    toState: "",
    toCity: "",
    toAddress: "",
    toEmail: "",
    toPhone: "",
  },
  feeInfo: {
    feePayer: "1",
    feePayType: "3",
  },
  applicantInfo: {
    applicantName: "",
    applicantTel: "",
  },
  eBopInfo: {
    paymentType: "",
    unitCode: "",
    residentCountry: "",
    residentCountryName: "",
    paymentUnderBondedGoods: "",
    paymentTerm: "",
    paymentNature: "",
    transactionCode1: "",
    transactionRemark1: "",
    amount1: "",
    currency1: "",
    transactionCode2: "",
    transactionRemark2: "",
    amount2: "",
    currency2: "",
    contractNo: "",
    invoiceNo: "",
    safeApprovalNo: "",
  },
});

/**
 * 仅产出 API 约定的嵌套结构。优先读嵌套路径；若存在历史 bug 写在根上的同名片段，则并入嵌套（避免旧会话脏数据）。
 * 同时丢弃 formState 根级误写入的扁平键，避免 POST body 混用扁平与嵌套。
 */
const normalizeCrossPayFormState = (
  raw: Record<string, unknown>
): CrossPayFormState => {
  const r = (nestedPath: string, flatKey?: string): string => {
    let v = lodashGet(raw, nestedPath);
    if (v !== undefined && v !== null && String(v).trim() !== "")
      return String(v);
    if (flatKey !== undefined) {
      v = lodashGet(raw, flatKey);
      if (v !== undefined && v !== null) return String(v);
    }
    return "";
  };
  return {
    businessTransNo: r("businessTransNo"),
    payAmount: r("payAmount"),
    payCurrency: r("payCurrency"),
    remitType: r("remitType") || "0",
    purchaseAmount: r("purchaseAmount"),
    crossBank: r("crossBank"),
    crossCity: r("crossCity"),
    scheduleDate: r("scheduleDate"),
    bankApprovalFlag: r("bankApprovalFlag") || "0",
    purpose: r("purpose"),
    postscript: r("postscript"),
    summary: r("summary"),
    priority: r("priority") || "1",
    eBopFlag: r("eBopFlag") || "0",
    ignoreCheckBop: r("ignoreCheckBop"),
    fromAccountInfo: {
      fromAccountNo: r("fromAccountInfo.fromAccountNo", "fromAccountNo"),
      fromAccountName: r("fromAccountInfo.fromAccountName", "fromAccountName"),
      fromAccountEnglishName: r(
        "fromAccountInfo.fromAccountEnglishName",
        "fromAccountEnglishName"
      ),
      fromAccountCurrency: r(
        "fromAccountInfo.fromAccountCurrency",
        "fromAccountCurrency"
      ),
      fromAccountType:
        r("fromAccountInfo.fromAccountType", "fromAccountType") || "00",
    },
    fromBankInfo: {
      fromBankSwiftCode: r(
        "fromBankInfo.fromBankSwiftCode",
        "fromBankSwiftCode"
      ),
      fromBankLocalCode: r(
        "fromBankInfo.fromBankLocalCode",
        "fromBankLocalCode"
      ),
      fromBankBranchCode: r(
        "fromBankInfo.fromBankBranchCode",
        "fromBankBranchCode"
      ),
      fromBankName: r("fromBankInfo.fromBankName", "fromBankName"),
      fromBankBranchName: r(
        "fromBankInfo.fromBankBranchName",
        "fromBankBranchName"
      ),
      fromBankCountry: r("fromBankInfo.fromBankCountry", "fromBankCountry"),
      fromBankAddress: r("fromBankInfo.fromBankAddress", "fromBankAddress"),
    },
    payerInfo: {
      fromCountry: r("payerInfo.fromCountry", "fromCountry"),
      fromState: r("payerInfo.fromState", "fromState"),
      fromCity: r("payerInfo.fromCity", "fromCity"),
      fromAddress: r("payerInfo.fromAddress", "fromAddress"),
      fromUnitCode: r("payerInfo.fromUnitCode", "fromUnitCode"),
      fromIndividualNo: r("payerInfo.fromIndividualNo", "fromIndividualNo"),
      fromIndividualType: r(
        "payerInfo.fromIndividualType",
        "fromIndividualType"
      ),
    },
    toAccountInfo: {
      toAccountNo: r("toAccountInfo.toAccountNo", "toAccountNo"),
      toAccountName: r("toAccountInfo.toAccountName", "toAccountName"),
      toAccountType:
        r("toAccountInfo.toAccountType", "toAccountType") || "0",
      toAccountCurrency: r(
        "toAccountInfo.toAccountCurrency",
        "toAccountCurrency"
      ),
    },
    toBankInfo: {
      toBankSwiftCode: r("toBankInfo.toBankSwiftCode", "toBankSwiftCode"),
      toBankLocalCode: r("toBankInfo.toBankLocalCode", "toBankLocalCode"),
      toBankBranchCode: r("toBankInfo.toBankBranchCode", "toBankBranchCode"),
      toBankName: r("toBankInfo.toBankName", "toBankName"),
      toBankCountry: r("toBankInfo.toBankCountry", "toBankCountry"),
      toBankCountryName: r(
        "toBankInfo.toBankCountryName",
        "toBankCountryName"
      ),
      toBankAddress: r("toBankInfo.toBankAddress", "toBankAddress"),
    },
    payeeInfo: {
      toCountry: r("payeeInfo.toCountry", "toCountry"),
      toCountryName: r("payeeInfo.toCountryName", "toCountryName"),
      toState: r("payeeInfo.toState", "toState"),
      toCity: r("payeeInfo.toCity", "toCity"),
      toAddress: r("payeeInfo.toAddress", "toAddress"),
      toEmail: r("payeeInfo.toEmail", "toEmail"),
      toPhone: r("payeeInfo.toPhone", "toPhone"),
    },
    feeInfo: {
      feePayer: r("feeInfo.feePayer", "feePayer"),
      feePayType: r("feeInfo.feePayType", "feePayType") || "3",
    },
    applicantInfo: {
      applicantName: r("applicantInfo.applicantName", "applicantName"),
      applicantTel: r("applicantInfo.applicantTel", "applicantTel"),
    },
    eBopInfo: {
      paymentType: r("eBopInfo.paymentType", "paymentType"),
      unitCode: r("eBopInfo.unitCode", "unitCode"),
      residentCountry: r("eBopInfo.residentCountry", "residentCountry"),
      residentCountryName: r(
        "eBopInfo.residentCountryName",
        "residentCountryName"
      ),
      paymentUnderBondedGoods: r(
        "eBopInfo.paymentUnderBondedGoods",
        "paymentUnderBondedGoods"
      ),
      paymentTerm: r("eBopInfo.paymentTerm", "paymentTerm"),
      paymentNature: r("eBopInfo.paymentNature", "paymentNature"),
      transactionCode1: r("eBopInfo.transactionCode1", "transactionCode1"),
      transactionRemark1: r(
        "eBopInfo.transactionRemark1",
        "transactionRemark1"
      ),
      amount1: r("eBopInfo.amount1", "amount1"),
      currency1: r("eBopInfo.currency1", "currency1"),
      transactionCode2: r("eBopInfo.transactionCode2", "transactionCode2"),
      transactionRemark2: r(
        "eBopInfo.transactionRemark2",
        "transactionRemark2"
      ),
      amount2: r("eBopInfo.amount2", "amount2"),
      currency2: r("eBopInfo.currency2", "currency2"),
      contractNo: r("eBopInfo.contractNo", "contractNo"),
      invoiceNo: r("eBopInfo.invoiceNo", "invoiceNo"),
      safeApprovalNo: r("eBopInfo.safeApprovalNo", "safeApprovalNo"),
    },
  };
};

const formState = ref<CrossPayFormState>(initialFormState());

const fieldGet = (path: string) => {
  const v = lodashGet(formState.value, path);
  if (v === undefined || v === null) return "";
  return String(v);
};

const fieldSet = (path: string, val: string) => {
  lodashSet(formState.value as unknown as Record<string, unknown>, path, val);
};

type GlobalSubmitFormField = {
  path: string;
  required: boolean;
  placeholder: string;
  conditional?: boolean;
  hint?: string;
  control?: "input" | "select";
  options?: { value: string; label: string }[];
};

/** 分组内 path 为短名（如 fromAccountNo）；与 pathPrefix 拼成 lodash 路径（如 fromAccountInfo.fromAccountNo） */
const joinFieldPath = (pathPrefix: string | undefined, path: string) => {
  const p = (pathPrefix ?? "").trim();
  return p ? `${p}.${path}` : path;
};

type GlobalSubmitFormGroup = {
  title: string;
  /** 嵌套对象在 formState 上的前缀，如 fromAccountInfo；根级分组省略 */
  pathPrefix?: string;
  required: boolean;
  fields: GlobalSubmitFormField[];
};

const formGroups: GlobalSubmitFormGroup[] = [
  {
    title: "根级字段",
    required: true,
    fields: [
      { path: "businessTransNo", required: true, placeholder: "业务流水号，全局唯一" },
      { path: "payAmount", required: true, placeholder: "付款金额，最多两位小数" },
      { path: "payCurrency", required: true, placeholder: "付款币别，如 USD" },
      {
        path: "remitType",
        required: false,
        placeholder: "",
        control: "select",
        options: [
          { value: "0", label: "0 - 现汇" },
          { value: "1", label: "1 - 购汇" },
          { value: "2", label: "2 - 其他" },
        ],
      },
      { path: "purchaseAmount", required: false, placeholder: "购汇金额（remitType=1 时必填）", hint: "选择购汇时必填" },
      {
        path: "crossBank",
        required: false,
        placeholder: "",
        control: "select",
        options: [
          { value: "", label: "未指定" },
          { value: "0", label: "0 - 本行" },
          { value: "1", label: "1 - 他行" },
        ],
      },
      {
        path: "crossCity",
        required: false,
        placeholder: "",
        control: "select",
        options: [
          { value: "", label: "未指定" },
          { value: "0", label: "0 - 同城" },
          { value: "1", label: "1 - 异地" },
        ],
      },
      { path: "scheduleDate", required: true, placeholder: "计划付款日期 yyyyMMdd" },
      {
        path: "bankApprovalFlag",
        required: false,
        placeholder: "",
        control: "select",
        options: [
          { value: "0", label: "0 - 直接支付" },
          { value: "1", label: "1 - 需要网银复核" },
        ],
      },
      { path: "purpose", required: false, placeholder: "付款用途" },
      { path: "postscript", required: false, placeholder: "附言" },
      { path: "summary", required: false, placeholder: "摘要" },
      {
        path: "priority",
        required: false,
        placeholder: "",
        control: "select",
        options: [
          { value: "1", label: "1 - 普通" },
          { value: "2", label: "2 - 加急" },
        ],
      },
      {
        path: "eBopFlag",
        required: false,
        placeholder: "",
        control: "select",
        options: [
          { value: "0", label: "0 - 不申报" },
          { value: "1", label: "1 - 申报" },
        ],
      },
      { path: "ignoreCheckBop", required: false, placeholder: "传 1 忽略 BOP 校验" },
    ],
  },
  {
    title: "fromAccountInfo",
    pathPrefix: "fromAccountInfo",
    required: true,
    fields: [
      { path: "fromAccountNo", required: true, placeholder: "付款账号" },
      { path: "fromAccountName", required: true, placeholder: "付款账户名" },
      { path: "fromAccountEnglishName", required: false, placeholder: "账户英文名称" },
      { path: "fromAccountCurrency", required: true, placeholder: "付款账户币别" },
      {
        path: "fromAccountType",
        required: false,
        placeholder: "",
        control: "select",
        options: [
          { value: "00", label: "00 - 对公" },
          { value: "01", label: "01 - 对私" },
        ],
      },
    ],
  },
  {
    title: "fromBankInfo",
    pathPrefix: "fromBankInfo",
    required: true,
    fields: [
      { path: "fromBankSwiftCode", required: true, placeholder: "付款方 SWIFT/BIC" },
      { path: "fromBankLocalCode", required: false, placeholder: "联行号（地方清算）" },
      { path: "fromBankBranchCode", required: false, placeholder: "付款方分行代码" },
      { path: "fromBankName", required: true, placeholder: "付款账户银行" },
      { path: "fromBankBranchName", required: false, placeholder: "付款账户银行分行名" },
      { path: "fromBankCountry", required: true, placeholder: "付款账户开户地/国，如 CN" },
      { path: "fromBankAddress", required: false, placeholder: "付款账户开户行地址" },
    ],
  },
  {
    title: "payerInfo",
    pathPrefix: "payerInfo",
    required: false,
    fields: [
      { path: "fromCountry", required: false, placeholder: "付款人所在国" },
      { path: "fromState", required: false, placeholder: "州/省" },
      { path: "fromCity", required: false, placeholder: "城市" },
      { path: "fromAddress", required: false, placeholder: "地址" },
      { path: "fromUnitCode", required: false, conditional: true, placeholder: "组织机构代码（对公 00 时必填）" },
      { path: "fromIndividualNo", required: false, conditional: true, placeholder: "身份证件号码（对私 01 时必填）" },
      { path: "fromIndividualType", required: false, conditional: true, placeholder: "证件类型（对私 01 时必填）" },
    ],
  },
  {
    title: "toAccountInfo",
    pathPrefix: "toAccountInfo",
    required: true,
    fields: [
      { path: "toAccountNo", required: true, placeholder: "收款账号" },
      { path: "toAccountName", required: true, placeholder: "收款账户名" },
      {
        path: "toAccountType",
        required: true,
        placeholder: "",
        control: "select",
        options: [
          { value: "0", label: "0 - 对公" },
          { value: "1", label: "1 - 对私" },
        ],
      },
      { path: "toAccountCurrency", required: true, placeholder: "收款账户币别" },
    ],
  },
  {
    title: "toBankInfo",
    pathPrefix: "toBankInfo",
    required: true,
    fields: [
      { path: "toBankSwiftCode", required: false, placeholder: "收款方 SWIFT/BIC" },
      { path: "toBankLocalCode", required: false, placeholder: "收款行行号/CNAPS" },
      { path: "toBankBranchCode", required: false, placeholder: "收款行分行代码" },
      { path: "toBankName", required: false, placeholder: "收款行名称" },
      { path: "toBankCountry", required: false, placeholder: "收款账户所在国，如 US" },
      { path: "toBankCountryName", required: false, placeholder: "收款账户国家名称" },
      { path: "toBankAddress", required: false, placeholder: "收款行地址" },
    ],
  },
  {
    title: "payeeInfo",
    pathPrefix: "payeeInfo",
    required: false,
    fields: [
      { path: "toCountry", required: false, placeholder: "收款人所在国" },
      { path: "toCountryName", required: false, placeholder: "收款人国家名称" },
      { path: "toState", required: false, placeholder: "州/省" },
      { path: "toCity", required: false, placeholder: "城市" },
      { path: "toAddress", required: false, placeholder: "地址" },
      { path: "toEmail", required: false, placeholder: "邮箱" },
      { path: "toPhone", required: false, placeholder: "电话" },
    ],
  },
  {
    title: "feeInfo",
    pathPrefix: "feeInfo",
    required: false,
    fields: [
      {
        path: "feePayer",
        required: false,
        placeholder: "",
        control: "select",
        options: [
          { value: "", label: "未指定" },
          { value: "1", label: "1 - 汇款人承担" },
          { value: "2", label: "2 - 收款人承担" },
          { value: "3", label: "3 - 共同承担" },
        ],
      },
      {
        path: "feePayType",
        required: false,
        placeholder: "",
        control: "select",
        options: [
          { value: "1", label: "1 - 现金" },
          { value: "2", label: "2 - 支票" },
          { value: "3", label: "3 - 账户（默认）" },
        ],
      }
    ],
  },
  {
    title: "applicantInfo",
    pathPrefix: "applicantInfo",
    required: false,
    fields: [
      { path: "applicantName", required: false, placeholder: "申请人姓名" },
      { path: "applicantTel", required: false, placeholder: "申请人电话" },
    ],
  },
  {
    title: "eBopInfo",
    pathPrefix: "eBopInfo",
    required: false,
    fields: [
      { path: "paymentType", required: false, placeholder: "O/D/X（海外外币/国内外币/跨境人民币）" },
      { path: "unitCode", required: false, placeholder: "主体标识码/组织机构代码" },
      { path: "residentCountry", required: false, placeholder: "收款人常驻国家代码" },
      { path: "residentCountryName", required: false, placeholder: "收款人常驻国家名称" },
      { path: "paymentUnderBondedGoods", required: false, placeholder: "报税货物项下付款 Y/N" },
      { path: "paymentTerm", required: false, placeholder: "付款方式 A/P/R/O" },
      { path: "paymentNature", required: false, placeholder: "付款性质 X/E/D/A/M/O" },
      { path: "transactionCode1", required: false, placeholder: "BOP 交易代码 1" },
      { path: "transactionRemark1", required: false, placeholder: "交易备注 1" },
      { path: "amount1", required: false, placeholder: "金额 1" },
      { path: "currency1", required: false, placeholder: "币种 1" },
      { path: "transactionCode2", required: false, placeholder: "BOP 交易代码 2" },
      { path: "transactionRemark2", required: false, placeholder: "交易备注 2" },
      { path: "amount2", required: false, placeholder: "金额 2" },
      { path: "currency2", required: false, placeholder: "币种 2" },
      { path: "contractNo", required: false, placeholder: "合同号" },
      { path: "invoiceNo", required: false, placeholder: "发票编号" },
      { path: "safeApprovalNo", required: false, placeholder: "备案号/安全批准号" },
    ],
  },
];

// 国密相关
const enableGm = ref(true);
const sm2PrivateKey = ref("");
const showGmHelp = ref(false);

let sm2Lib = sm.sm2;
let sm4Lib = sm.sm4;

const randomSm4Key = () => {
  const chars =
    "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";
  let s = "";
  for (let i = 0; i < 16; i++)
    s += chars[Math.floor(Math.random() * chars.length)];
  return s;
};
const convertUtf82HEX = (utf8: string) => {
  const array = new Uint8Array(utf8.length);
  let hexString: string = "";
  for (let i = 0; i < array.length; i++) {
    hexString += `0${array[i].toString(16)}`.slice(-2);
  }
  return hexString;
};

// Base64校验（标准Base64）
const isBase64 = (s: unknown) => {
  const str = String(s ?? "");
  if (!str || str.length % 4 !== 0) return false;
  if (!/^[A-Za-z0-9+/=]+$/.test(str)) return false;
  try {
    atob(str);
    return true;
  } catch {
    return false;
  }
};

const base64ToBytes = (b64: string) => {
  const s = String(b64 || "").replace(/\s+/g, "");
  const pad = s.length % 4 ? "=".repeat(4 - (s.length % 4)) : "";
  const raw = atob(s + pad);
  return new Uint8Array(Array.from(raw).map((c) => c.charCodeAt(0)));
};
const bytesToHex = (arr: Uint8Array) =>
  Array.from(arr)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
const hexToBytes = (hex: string) =>
  new Uint8Array(
    (hex.match(/.{1,2}/g) || []).map((b) => parseInt(b, 16))
  );
const hexToBase64 = (hex: string) => {
  const bytes = hexToBytes(hex);
  return btoa(String.fromCharCode(...Array.from(bytes)));
};
const readDerLength = (buf: Uint8Array, pos: number) => {
  let len = buf[pos];
  pos += 1;
  if ((len & 0x80) !== 0) {
    const n = len & 0x7f;
    let v = 0;
    for (let i = 0; i < n; i++) v = (v << 8) | buf[pos + i];
    pos += n;
    len = v;
  }
  return { len, pos };
};
const spkiBase64ToUncompressedHex = (b64: string) => {
  const outer = base64ToBytes(b64);
  if (outer[0] !== 0x30) throw new Error("公钥DER解析失败: 非SEQUENCE");
  const { len, pos } = readDerLength(outer, 1);
  const content = outer.subarray(pos, pos + len);
  let p = 0;
  if (content[p] !== 0x30) throw new Error("公钥DER解析失败: 算法标识缺失");
  p += 1;
  const r1 = readDerLength(content, p);
  p = r1.pos + r1.len;
  if (content[p] !== 0x03) throw new Error("公钥DER解析失败: 未找到BIT STRING");
  p += 1;
  const r2 = readDerLength(content, p);
  const bitStart = r2.pos;
  const bitLen = r2.len;
  const unused = content[bitStart];
  if (unused !== 0x00) throw new Error("公钥DER解析失败: 非零unused bits");
  const point = content.subarray(bitStart + 1, bitStart + bitLen);
  if (point.length !== 65 || point[0] !== 0x04) throw new Error("公钥格式错误: 需非压缩点");
  return bytesToHex(point);
};
const pkcs8Base64ToPrivateKeyHex = (b64: string) => {
  const buf = base64ToBytes(b64);
  let p = 0;
  if (buf[p++] !== 0x30) throw new Error("私钥DER解析失败: 非SEQUENCE");
  let r = readDerLength(buf, p);
  const end = r.pos + r.len;
  p = r.pos;
  if (buf[p++] !== 0x02) throw new Error("私钥DER解析失败: 缺少版本INTEGER");
  r = readDerLength(buf, p);
  p = r.pos + r.len;
  if (buf[p++] !== 0x30) throw new Error("私钥DER解析失败: 缺少算法SEQUENCE");
  r = readDerLength(buf, p);
  p = r.pos + r.len;
  if (buf[p++] !== 0x04) throw new Error("私钥DER解析失败: 未找到OCTET STRING");
  r = readDerLength(buf, p);
  const ecStart = r.pos;
  const ecLen = r.len;
  const ec = buf.subarray(ecStart, ecStart + ecLen);
  let q = 0;
  if (ec[q++] !== 0x30) throw new Error("私钥DER解析失败: 非ECPrivateKey SEQUENCE");
  let r2 = readDerLength(ec, q);
  const ecEnd = r2.pos + r2.len;
  q = r2.pos;
  if (ec[q++] !== 0x02) throw new Error("私钥DER解析失败: 缺少EC版本INTEGER");
  r2 = readDerLength(ec, q);
  q = r2.pos + r2.len;
  if (ec[q++] !== 0x04) throw new Error("私钥DER解析失败: 未找到ECPrivateKey OCTET STRING");
  r2 = readDerLength(ec, q);
  const privStart = r2.pos;
  const privLen = r2.len;
  let priv = ec.subarray(privStart, privStart + privLen);
  if (priv.length > 32) priv = priv.slice(priv.length - 32);
  if (priv.length < 32) priv = new Uint8Array([...new Array(32 - priv.length).fill(0), ...Array.from(priv)]);
  return bytesToHex(priv as Uint8Array);
};
const normalizeSm2PublicHex = (s: string) => {
  if (/^[A-Za-z0-9+/=]+$/.test(s)) return spkiBase64ToUncompressedHex(s);
  return s;
};
const normalizeSm2PrivateHex = (s: string) => {
  if (/^[A-Za-z0-9+/=]+$/.test(s)) return pkcs8Base64ToPrivateKeyHex(s);
  return s;
};

// 获取应用信息
const getApplicationInfo = async () => {
  try {
    const response = await httpService.get<any>(
      "/nxb/console/application/get"
    );
    if (response) {
      return {
        appId: response.appId,
        appSecret: response.appSecret,
        xencioPublicKey: response.xencioPublicKey,
      };
    }
    return null;
  } catch (error) {
    console.log("获取应用信息失败:", error);
    return null;
  }
};

// 获取客户信息
const getCustomerInfo = async () => {
  try {
    const response = await httpService.get<any>(
      "/nxb/console/customer/listWithSummary",
      {
        pageNow: 1,
        pageSize: 10,
      }
    );
    if (response && response.customers && response.customers.length > 0) {
      return response.customers[0].customerCode;
    }
    return null;
  } catch (error) {
    console.log("获取客户信息失败:", error);
    return null;
  }
};

// 登录后自动获取用户信息
const autoFetchUserInfo = async () => {
  const userInfoStr = localStorage.getItem("userInfo");
  if (userInfoStr) {
    const appInfo = await getApplicationInfo();
    if (appInfo) {
      appId.value = appInfo.appId;
      appSecret.value = appInfo.appSecret;
      xencioPublicKey.value = appInfo.xencioPublicKey;
    }
    const customerCodeValue = await getCustomerInfo();
    if (customerCodeValue) {
      customerCode.value = customerCodeValue;
    }
  }
};

onMounted(() => {
  void autoFetchUserInfo().finally(() => {
    formState.value = normalizeCrossPayFormState(
      JSON.parse(JSON.stringify(formState.value)) as Record<string, unknown>
    );
  });
});

watch(
  [
    appId,
    appSecret,
    customerCode,
    formState,
    metaJson,
    activeLanguage,
    enableGm,
    response,
  ],
  () => highlightCode(),
  { flush: "post", deep: true }
);

// 语言选项
const languages = [
  { id: "curl", name: "cURL" },
  { id: "javascript", name: "JavaScript" },
  { id: "python", name: "Python" },
  { id: "java", name: "Java" },
  { id: "csharp", name: "C#" },
  { id: "php", name: "PHP" },
  { id: "ruby", name: "Ruby" },
];

const displayAppSecretForSample = () =>
  appSecret.value && appSecret.value !== DEFAULT_APP_SECRET
    ? appSecret.value
    : "8201e508ef948b8fcdca855729e7ee34";

const deepOmitEmptyStrings = (obj: unknown): unknown => {
  if (obj === null || obj === undefined) return undefined;
  if (typeof obj === "string") return obj.trim() === "" ? undefined : obj;
  if (typeof obj !== "object") return obj;
  if (Array.isArray(obj)) {
    const arr = (obj as unknown[])
      .map(deepOmitEmptyStrings)
      .filter((x) => x !== undefined);
    return arr.length ? arr : undefined;
  }
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
    const p = deepOmitEmptyStrings(v);
    if (p === undefined) continue;
    if (
      typeof p === "object" &&
      p !== null &&
      !Array.isArray(p) &&
      Object.keys(p as object).length === 0
    )
      continue;
    out[k] = p;
  }
  return Object.keys(out).length ? out : undefined;
};

const buildRequestPayloadForDisplay = (): Record<string, unknown> => {
  const normalized = normalizeCrossPayFormState(
    JSON.parse(JSON.stringify(formState.value)) as Record<string, unknown>
  );
  const base = deepOmitEmptyStrings(normalized);
  const payload =
    typeof base === "object" && base !== null
      ? { ...(base as Record<string, unknown>) }
      : {};
  const t = metaJson.value.trim();
  if (t) {
    payload.metaJson = t;
  }
  return payload;
};

const buildRequestPayload = (): Record<string, unknown> => {
  const normalized = normalizeCrossPayFormState(
    JSON.parse(JSON.stringify(formState.value)) as Record<string, unknown>
  );
  const pruned = deepOmitEmptyStrings(normalized);
  if (!pruned || typeof pruned !== "object") {
    throw new Error("请先填写必填字段");
  }
  const payload = { ...(pruned as Record<string, unknown>) };
  const t = metaJson.value.trim();
  if (t) {
    payload.metaJson = t;
  }
  return payload;
};

const getRequestText = () => {
  const payload = buildRequestPayloadForDisplay();
  const payloadJson = JSON.stringify(payload, null, 2);
  const displayAppId = appId.value || "4d9414e89bc24b0d89b678d9f20bc56a";
  const displayCustomerCode = customerCode.value || "CUSTFA48DD8EFF437956";
  const displayAppSecret = displayAppSecretForSample();

  switch (activeLanguage.value) {
    case "curl":
      return `curl -X POST '${serverUrl}/api/v2/payment/crossPay' \\\n  -H 'x-app-id: ${displayAppId}' \\\n  -H 'x-app-secret: ${displayAppSecret}' \\\n  -H 'x-customer-code: ${displayCustomerCode}' \\\n  -H 'Content-Type: application/json' \\\n  -d '${JSON.stringify(payload)}'`;

    case "javascript":
      return `const axios = require('axios');\n\nconst data = ${payloadJson};\n\nconst config = {\n  method: 'post',\n  url: '${serverUrl}/api/v2/payment/crossPay',\n  headers: {\n    'x-app-id': '${displayAppId}',\n    'x-app-secret': '${displayAppSecret}',\n    'x-customer-code': '${displayCustomerCode}',\n    'Content-Type': 'application/json'\n  },\n  data: data\n};\n\naxios(config)\n  .then(function (response) {\n    console.log(JSON.stringify(response.data));\n  })\n  .catch(function (error) {\n    console.log(error);\n  });`;

    case "python":
      return `import requests\nimport json\n\nurl = "${serverUrl}/api/v2/payment/crossPay"\n\npayload = ${payloadJson}\n\nheaders = {\n  'x-app-id': '${displayAppId}',\n  'x-app-secret': '${displayAppSecret}',\n  'x-customer-code': '${displayCustomerCode}',\n  'Content-Type': 'application/json'\n}\n\nresponse = requests.post(url, headers=headers, json=payload)\nprint(response.text)`;

    case "java":
      return `OkHttpClient client = new OkHttpClient();\n\nString json = ${JSON.stringify(JSON.stringify(payload))};\nRequestBody body = RequestBody.create(json, MediaType.parse("application/json"));\nRequest request = new Request.Builder()\n    .url("${serverUrl}/api/v2/payment/crossPay")\n    .post(body)\n    .addHeader("x-app-id", "${displayAppId}")\n    .addHeader("x-app-secret", "${displayAppSecret}")\n    .addHeader("x-customer-code", "${displayCustomerCode}")\n    .build();\nResponse response = client.newCall(request).execute();\nSystem.out.println(response.body().string());`;

    case "csharp":
      return `var client = new RestClient("${serverUrl}/api/v2/payment/crossPay");\nvar request = new RestRequest(Method.POST);\nrequest.AddHeader("x-app-id", "${displayAppId}");\nrequest.AddHeader("x-app-secret", "${displayAppSecret}");\nrequest.AddHeader("x-customer-code", "${displayCustomerCode}");\nrequest.AddHeader("Content-Type", "application/json");\nrequest.AddParameter("application/json", ${JSON.stringify(JSON.stringify(payload))}, ParameterType.RequestBody);\nIRestResponse response = client.Execute(request);\nConsole.WriteLine(response.Content);`;

    case "php":
      return `<?php\n\n$curl = curl_init();\n\ncurl_setopt_array($curl, array(\n    CURLOPT_URL => '${serverUrl}/api/v2/payment/crossPay',\n    CURLOPT_RETURNTRANSFER => true,\n    CURLOPT_CUSTOMREQUEST => 'POST',\n    CURLOPT_POSTFIELDS => ${JSON.stringify(JSON.stringify(payload))},\n    CURLOPT_HTTPHEADER => array(\n        'x-app-id: ${displayAppId}',\n        'x-app-secret: ${displayAppSecret}',\n        'x-customer-code: ${displayCustomerCode}',\n        'Content-Type: application/json'\n    ),\n));\n\n$response = curl_exec($curl);\ncurl_close($curl);\necho $response;`;

    case "ruby":
      return `require "uri"\nrequire "net/http"\nrequire "json"\n\nurl = URI("${serverUrl}/api/v2/payment/crossPay")\nhttps = Net::HTTP.new(url.host, url.port)\nhttps.use_ssl = true\nrequest = Net::HTTP::Post.new(url)\nrequest["x-app-id"] = "${displayAppId}"\nrequest["x-app-secret"] = "${displayAppSecret}"\nrequest["x-customer-code"] = "${displayCustomerCode}"\nrequest["Content-Type"] = "application/json"\nrequest.body = ${JSON.stringify(JSON.stringify(payload))}\nresponse = https.request(request)\nputs response.body`;

    default:
      return "";
  }
};

// 国密加密示例
const getGmSampleText = () => {
  const payload = buildRequestPayloadForDisplay();
  const displayAppId = appId.value || "4d9414e89bc24b0d89b678d9f20bc56a";
  const displayCustomerCode = customerCode.value || "CUSTFA48DD8EFF437956";
  const displayAppSecret = displayAppSecretForSample();

  switch (activeLanguage.value) {
    case "curl":
      return `# 国密加密请求示例（SM2/SM3/SM4）
# 步骤：1.生成随机SM4密钥 → 2.SM4加密业务JSON → 3.SM3摘要 → 4.SM2签名 → 5.SM2加密SM4密钥
curl -X POST '${serverUrl}/api/v2/payment/crossPay' \\
  -H 'x-app-id: ${displayAppId}' \\
  -H 'x-app-secret: ${displayAppSecret}' \\
  -H 'x-customer-code: ${displayCustomerCode}' \\
  -H 'xencio-key: <SM2加密后的SM4密钥>' \\
  -H 'Content-Type: application/json' \\
  -d '{"reqData":"<SM4密文>","sign":"<SM2签名>"}'`;

    case "javascript":
      return `// 国密加密请求示例（SM2/SM3/SM4）
// 使用 sm-crypto 库: npm install sm-crypto
const sm = require('sm-crypto');
const axios = require('axios');

const payload = ${JSON.stringify(payload, null, 2)};
const rawJson = JSON.stringify(payload);

// 1. 生成随机SM4密钥（16字节字符串）
const chars = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
let sm4Key = '';
for (let i = 0; i < 16; i++) sm4Key += chars[Math.floor(Math.random() * chars.length)];

// 2. SM4加密（需将sm4Key转hex）
const sm4KeyHex = Array.from(new TextEncoder().encode(sm4Key))
  .map(b => b.toString(16).padStart(2, '0')).join('');
const reqData = sm.sm4.encrypt(rawJson, sm4KeyHex);

// 3. SM3摘要
const signDigest = sm.sm3(rawJson);

// 4. SM2签名（私钥支持Base64(DER)或HEX）
const sign = sm.sm2.doSignature(signDigest, sm2PrivateKeyHex, { hash: false });

// 5. SM2加密SM4密钥
const xencioKey = sm.sm2.doEncrypt(sm4Key, xencioPublicKeyHex, 1);

// 发送请求
axios({
  method: 'post',
  url: '${serverUrl}/api/v2/payment/crossPay',
  headers: {
    'x-app-id': '${displayAppId}',
    'x-app-secret': '${displayAppSecret}',
    'x-customer-code': '${displayCustomerCode}',
    'xencio-key': xencioKey,
    'Content-Type': 'application/json'
  },
  data: { reqData, sign }
}).then(res => console.log(JSON.stringify(res.data))).catch(err => console.log(err));`;

    case "python":
      return `# 国密加密请求示例（SM2/SM3/SM4）
# 使用 gmssl 库: pip install gmssl
import json, random, string, requests
from gmssl import sm2, sm3, sm4

# 业务数据
payload = ${JSON.stringify(payload, null, 2)}
raw_json = json.dumps(payload, ensure_ascii=False)

# 1. 生成随机SM4密钥（16字节）
sm4_key = ''.join(random.choices(string.ascii_letters + string.digits, k=16))

# 2. SM4加密（ECB模式，PKCS#7填充）
sm4_crypt = sm4.CryptSM4()
sm4_crypt.set_key(sm4_key.encode(), sm4.SM4_ENCRYPT)
req_data = sm4_crypt.crypt_ecb(raw_json.encode()).hex()

# 3. SM3摘要
sign_digest = sm3.sm3_hash(raw_json.encode())

# 4. SM2签名
sm2_crypt = sm2.CryptSM2(private_key=sm2_private_hex, public_key=xencio_public_hex)
sign = sm2_crypt.sign(sign_digest)

# 5. SM2加密SM4密钥
xencio_key = sm2_crypt.encrypt(sm4_key.encode())

# 发送请求
response = requests.post(
    '${serverUrl}/api/v2/payment/crossPay',
    headers={
        'x-app-id': '${displayAppId}',
        'x-app-secret': '${displayAppSecret}',
        'x-customer-code': '${displayCustomerCode}',
        'xencio-key': xencio_key,
        'Content-Type': 'application/json'
    },
    json={'reqData': req_data, 'sign': sign}
)
print(response.text)`;

    case "java":
      return `// 国密加密请求示例（SM2/SM3/SM4）
// 使用 BouncyCastle (bcprov-jdk15on) + 国密扩展
import org.bouncycastle.jce.provider.BouncyCastleProvider;
import javax.crypto.Cipher;
import javax.crypto.spec.SecretKeySpec;
import java.security.*;
import java.nio.charset.StandardCharsets;

// 业务数据
String payload = ${JSON.stringify(JSON.stringify(payload))};

// 1. 生成随机SM4密钥（16字节）
SecureRandom random = new SecureRandom();
byte[] sm4Key = new byte[16];
random.nextBytes(sm4Key);

// 2. SM4加密（ECB模式，PKCS#7填充）
Security.addProvider(new BouncyCastleProvider());
Cipher cipher = Cipher.getInstance("SM4/ECB/PKCS7Padding", "BC");
cipher.init(Cipher.ENCRYPT_MODE, new SecretKeySpec(sm4Key, "SM4"));
String reqData = Hex.toHexString(cipher.doFinal(payload.getBytes(StandardCharsets.UTF_8)));

// 3. SM3摘要
MessageDigest sm3 = MessageDigest.getInstance("SM3", "BC");
byte[] digest = sm3.digest(payload.getBytes(StandardCharsets.UTF_8));

// 4. SM2签名
String sign = SM2.sign(digest, sm2PrivateKey);

// 5. SM2加密SM4密钥
String xencioKey = SM2.encrypt(sm4Key, xencioPublicKey);

// 发送请求
OkHttpClient client = new OkHttpClient();
String json = String.format("{\\"reqData\\":\\"%s\\",\\"sign\\":\\"%s\\"}", reqData, sign);
RequestBody body = RequestBody.create(json, MediaType.parse("application/json"));
Request request = new Request.Builder()
    .url("${serverUrl}/api/v2/payment/crossPay")
    .post(body)
    .addHeader("x-app-id", "${displayAppId}")
    .addHeader("x-app-secret", "${displayAppSecret}")
    .addHeader("x-customer-code", "${displayCustomerCode}")
    .addHeader("xencio-key", xencioKey)
    .build();
Response response = client.newCall(request).execute();
System.out.println(response.body().string());`;

    case "csharp":
      return `// 国密加密请求示例（SM2/SM3/SM4）
// 使用 Savorboard.GmSm 或其他国密库
using System.Security.Cryptography;
using System.Text;
using RestSharp;

// 业务数据
var payload = ${JSON.stringify(JSON.stringify(payload))};

// 1. 生成随机SM4密钥（16字节）
var sm4Key = new byte[16];
RandomNumberGenerator.Fill(sm4Key);

// 2. SM4加密（ECB模式，PKCS#7填充）
var sm4 = new SM4CryptoServiceProvider { Key = sm4Key, Mode = CipherMode.ECB, Padding = PaddingMode.PKCS7 };
var plainBytes = Encoding.UTF8.GetBytes(payload);
var encrypted = sm4.CreateEncryptor().TransformFinalBlock(plainBytes, 0, plainBytes.Length);
var reqData = Convert.ToHexString(encrypted).ToLower();

// 3. SM3摘要
var sm3 = new SM3CryptoServiceProvider();
var digest = sm3.ComputeHash(plainBytes);

// 4. SM2签名
var sm2 = new SM2CryptoServiceProvider();
sm2.ImportParameters(sm2PrivateParameters);
var sign = sm2.SignData(digest);

// 5. SM2加密SM4密钥（需将sm4Key转字符串）
var xencioKey = sm2.Encrypt(Encoding.UTF8.GetString(sm4Key));

// 发送请求
var client = new RestClient("${serverUrl}/api/v2/payment/crossPay");
var request = new RestRequest(Method.POST);
request.AddHeader("x-app-id", "${displayAppId}");
request.AddHeader("x-app-secret", "${displayAppSecret}");
request.AddHeader("x-customer-code", "${displayCustomerCode}");
request.AddHeader("xencio-key", xencioKey);
request.AddHeader("Content-Type", "application/json");
request.AddJsonBody(new { reqData, sign });
IRestResponse response = client.Execute(request);
Console.WriteLine(response.Content);`;

    case "php":
      return `<?php
// 国密加密请求示例（SM2/SM3/SM4）
// 使用 lpilp/guomi 或其他国密库: composer require lpilp/guomi
require_once 'vendor/autoload.php';

// 业务数据
$payload = ${JSON.stringify(JSON.stringify(payload))};

// 1. 生成随机SM4密钥（16字节）
$sm4Key = bin2hex(random_bytes(16));

// 2. SM4加密
$sm4 = new \\Gm\\SM4($sm4Key);
$reqData = bin2hex($sm4->encrypt($payload));

// 3. SM3摘要
$sm3 = new \\Gm\\SM3();
$signDigest = $sm3->hash($payload);

// 4. SM2签名
$sm2 = new \\Gm\\SM2($sm2PrivateKey, $xencioPublicKey);
$sign = $sm2->sign($signDigest);

// 5. SM2加密SM4密钥
$xencioKey = $sm2->encrypt($sm4Key);

// 发送请求
$curl = curl_init();
curl_setopt_array($curl, array(
    CURLOPT_URL => '${serverUrl}/api/v2/payment/crossPay',
    CURLOPT_RETURNTRANSFER => true,
    CURLOPT_CUSTOMREQUEST => 'POST',
    CURLOPT_POSTFIELDS => json_encode(array(
        'reqData' => $reqData,
        'sign' => $sign
    )),
    CURLOPT_HTTPHEADER => array(
        'x-app-id: ${displayAppId}',
        'x-app-secret: ${displayAppSecret}',
        'x-customer-code: ${displayCustomerCode}',
        'xencio-key: ' . $xencioKey,
        'Content-Type: application/json'
    ),
));
$response = curl_exec($curl);
curl_close($curl);
echo $response;`;

    case "ruby":
      return `# 国密加密请求示例（SM2/SM3/SM4）
# 使用本地国密库或OpenSSL 3.0+国密扩展
require "uri"
require "net/http"
require "json"
require "securerandom"

# 业务数据
payload = ${JSON.stringify(JSON.stringify(payload))}

# 1. 生成随机SM4密钥（16字节）
sm4_key = SecureRandom.alphanumeric(16)

# 2. SM4加密（调用国密扩展或本地服务）
req_data = sm4_encrypt(payload, sm4_key)

# 3. SM3摘要
sign_digest = sm3_hash(payload)

# 4. SM2签名
sign = sm2_sign(sign_digest, sm2_private_key_hex)

# 5. SM2加密SM4密钥
xencio_key = sm2_encrypt(sm4_key, xencio_public_key_hex)

# 发送请求
url = URI("${serverUrl}/api/v2/payment/crossPay")
https = Net::HTTP.new(url.host, url.port)
https.use_ssl = true
request = Net::HTTP::Post.new(url)
request["x-app-id"] = "${displayAppId}"
request["x-app-secret"] = "${displayAppSecret}"
request["x-customer-code"] = "${displayCustomerCode}"
request["xencio-key"] = xencio_key
request["Content-Type"] = "application/json"
request.body = { reqData: req_data, sign: sign }.to_json
response = https.request(request)
puts response.body`;

    default:
      return "";
  }
};

// ── 必填字段（lodash 路径，与对外 API 一致） ──
const requiredPaths = [
  "businessTransNo",
  "payAmount",
  "payCurrency",
  "scheduleDate",
  "fromAccountInfo.fromAccountName",
  "fromBankInfo.fromBankName",
  "toAccountInfo.toAccountNo",
  "toAccountInfo.toAccountName",
  "toAccountInfo.toAccountType",
];

const isPathEmpty = (path: string) => {
  const v = lodashGet(formState.value, path);
  return v === undefined || v === null || String(v).trim() === "";
};

const submitPayment = async () => {
  showValidation.value = true;

  if (!appId.value?.trim() || !appSecret.value?.trim() || !customerCode.value?.trim()) {
    alert("请填写 x-app-id、x-app-secret、x-customer-code");
    return;
  }

  const missing = requiredPaths.filter(isPathEmpty);
  if (missing.length) {
    alert(`请填写必填字段：${missing.join("、")}`);
    return;
  }

  const fromAccountType = (formState.value.fromAccountInfo.fromAccountType || "00").trim();
  if (fromAccountType === "00" && isPathEmpty("payerInfo.fromUnitCode")) {
    alert("付款账户类型为对公（00）时，付款人组织机构代码（payerInfo.fromUnitCode）必填");
    return;
  }
  if (
    fromAccountType === "01" &&
    (isPathEmpty("payerInfo.fromIndividualNo") ||
      isPathEmpty("payerInfo.fromIndividualType"))
  ) {
    alert("付款账户类型为对私（01）时，付款人证件号码与证件类型（payerInfo）必填");
    return;
  }

  if (formState.value.remitType === "1" && isPathEmpty("purchaseAmount")) {
    alert("支付方式为购汇（remitType=1）时，购汇金额 purchaseAmount 必填");
    return;
  }

  let payloadObj: Record<string, unknown>;
  try {
    payloadObj = buildRequestPayload();
  } catch (err) {
    alert((err as Error).message);
    return;
  }

  if (enableGm.value) {
    if (!xencioPublicKey.value?.trim() || !sm2PrivateKey.value?.trim()) {
      alert("请填写平台SM2公钥与企业SM2私钥");
      return;
    }
  }

  loading.value = true;
  response.value = "";

  try {
    const headers: Record<string, string> = {
      "x-app-id": appId.value,
      "x-app-secret": appSecret.value,
      "x-customer-code": customerCode.value,
      "Content-Type": "application/json",
    };
    let bodyToSend: any = null;

    if (enableGm.value) {
      const rawJson = JSON.stringify(payloadObj);
      const sm4Key = randomSm4Key();
      const sm3Lib = sm.sm3;
      const sm4KeyHex = convertUtf82HEX(sm4Key);

      const reqData = sm4Lib.encrypt(rawJson, sm4KeyHex);
      const signDigest = sm3Lib(rawJson);
      const sm2PrivHex = normalizeSm2PrivateHex(sm2PrivateKey.value);
      const xencioPubHex = normalizeSm2PublicHex(xencioPublicKey.value);
      const sign = sm2Lib.doSignature(signDigest, sm2PrivHex, { hash: false });
      const xencioKeyHeader = sm2Lib.doEncrypt(sm4Key, xencioPubHex, 1);

      headers["xencio-key"] = xencioKeyHeader;
      bodyToSend = { reqData, sign };
    } else {
      bodyToSend = payloadObj;
    }

    const res = await fetch(`${serverUrl}/api/v2/payment/crossPay`, {
      method: "POST",
      headers,
      body: JSON.stringify(bodyToSend),
    });

    const text = await res.text();

    if (enableGm.value) {
      let obj: any;
      try { obj = JSON.parse(text); } catch { response.value = text; return; }

      if (obj && obj.respData) {
        const encKeyHeader = res.headers.get("xencio-key") || res.headers.get("XENCIO-KEY");
        if (!encKeyHeader) {
          response.value = JSON.stringify({ error: "响应缺少xencio-key头" }, null, 2);
          return;
        }
        const respSm4Key = sm2Lib.doDecrypt(encKeyHeader, normalizeSm2PrivateHex(sm2PrivateKey.value), 1);
        const respSm4KeyHex = convertUtf82HEX(respSm4Key);
        const originResp = sm4Lib.decrypt(obj.respData, respSm4KeyHex);
        try {
          response.value = JSON.stringify({ originResp: JSON.parse(originResp) }, null, 2);
        } catch {
          response.value = JSON.stringify({ originResp }, null, 2);
        }
      } else {
        response.value = text;
      }
    } else {
      try {
        response.value = JSON.stringify(JSON.parse(text), null, 2);
      } catch {
        response.value = text;
      }
    }
  } catch (error) {
    response.value = JSON.stringify({ error: "请求失败: " + (error as Error).message }, null, 2);
  } finally {
    loading.value = false;
  }
};

</script>
